<template>
  <BaseModal class="base-overlay-first main-base-modal-first beauty-audit-modal-base">
    <div class="modal-columns">
      <!-- Left Column: Steps -->
      <div class="modal-steps-col">
        <h3 class="modal-title-left">Поможем настроить всё за вас</h3>
        <div class="modal-steps-list">
          <div
            class="modal-step-item"
            v-for="(step, idx) in migrationSteps"
            :key="idx"
          >
            <div class="modal-step-icon-wrapper">
              <svg
                v-if="idx === 0"
                class="modal-step-svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line
                  x1="16"
                  y1="13"
                  x2="8"
                  y2="13"
                ></line>
                <line
                  x1="16"
                  y1="17"
                  x2="8"
                  y2="17"
                ></line>
              </svg>
              <svg
                v-else-if="idx === 1"
                class="modal-step-svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
              >
                <path
                  d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"
                ></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line
                  x1="12"
                  y1="22.08"
                  x2="12"
                  y2="12"
                ></line>
              </svg>
              <svg
                v-else-if="idx === 2"
                class="modal-step-svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
              >
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
              <div class="modal-step-badge">{{ idx + 1 }}</div>
            </div>
            <div class="modal-step-info">
              <div class="modal-step-title">{{ step.title }}</div>
              <div class="modal-step-desc">{{ step.desc }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Form -->
      <div class="modal-form-col">
        <h3 class="modal-title-right">Оставьте заявку</h3>

        <form
          @submit.prevent="submitAuditRequest"
          class="modal-form-fields"
        >
          <div class="form-group">
            <input
              v-model="auditForm.name"
              type="text"
              placeholder="Ваше имя"
              class="form-input"
              required
            />
          </div>
          <div class="form-group">
            <input
              v-model="auditForm.nickname"
              placeholder="Телефон или ник в Instagram/Telegram"
              class="form-input"
              required
            />
          </div>
          <div class="form-group">
            <textarea
              v-model="auditForm.comment"
              :placeholder="
                is_main
                  ? 'Опишите ваш бизнес (услуги, товары, с какого сервиса переходите)'
                  : is_product
                    ? 'Как вы работаете сейчас?'
                    : 'С какого сервиса переходите? (Altegio, Dikidi, Excel, блокнот)'
              "
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>
          <button
            type="submit"
            class="btn-primary btn-full-width"
            :disabled="formSubmitting"
          >
            {{ formSubmitting ? "Отправка..." : "Получить помощь с настройкой" }}
          </button>
        </form>
      </div>
    </div>
  </BaseModal>
</template>

<script lang="ts" setup>
const route = useRoute();
const is_product = computed(() => {
  return route.path.includes("ecommerce");
});
const is_main = computed(() => {
  return !route.path.includes("beauty") && !route.path.includes("ecommerce");
});

const migrationSteps = computed(() => {
  if (is_main.value) {
    return [
      {
        title: "Изучим ваш бизнес",
        desc: "Узнаем, какие товары или услуги вы предлагаете, и подберём оптимальный сценарий работы.",
      },
      {
        title: "Всё настроим",
        desc: "Добавим товары или услуги, подключим Telegram-бота и перенесём базу клиентов.",
      },
      {
        title: "Запустим",
        desc: "Проверим, что всё работает, и останемся на связи, если возникнут вопросы.",
      },
    ];
  }

  if (is_product.value) {
    return [
      {
        title: "Изучим ваш магазин",
        desc: "Посмотрим, как вы принимаете заказы и организован каталог товаров.",
      },
      {
        title: "Всё настроим",
        desc: "Загрузим товары, категории, цены, подключим Telegram-бота и перенесём базу клиентов.",
      },
      {
        title: "Запустим",
        desc: "Проверим работу магазина и останемся на связи, если понадобится помощь.",
      },
    ];
  }

  return [
    {
      title: "Изучим ваш бизнес",
      desc: "Посмотрим, как вы ведёте запись клиентов и организована работа.",
    },
    {
      title: "Всё настроим",
      desc: "Добавим услуги, сотрудников, перенесём клиентов и подключим Telegram-бота.",
    },
    {
      title: "Запустим",
      desc: "Проверим работу системы и будем рядом, если возникнут вопросы.",
    },
  ];
});

const formSubmitting = ref(false);
const auditForm = ref({
  name: "",
  nickname: "",
  comment: "",
});

const { mutate: mutateContactOrder, onDone: onContactOrderDone } = useMakeContactOrderDto();
const { closeModal } = useModalStore();

const submitAuditRequest = async () => {
  formSubmitting.value = true;
  try {
    const data = {
      name: auditForm.value.name,
      nickname: auditForm.value.nickname,
      question: auditForm.value.comment,
      type: "data transfer_" + (is_main.value ? "main" : is_product.value ? "product" : "service"),
    };
    await mutateContactOrder(data);
  } catch (e) {
    console.error(e);
    formSubmitting.value = false;
  }
};

onContactOrderDone(() => {
  formSubmitting.value = false;
  closeModal();
  auditForm.value.name = "";
  auditForm.value.nickname = "";
  auditForm.value.comment = "";

  try {
    const { openNotification } = useNotificationStore();
    openNotification("Заявка успешно отправлена! Мы свяжемся с вами в ближайшее время.");
  } catch (e) {
    console.error(e);
  }
});
</script>

<style scoped>
@media (--desktop) {
  :deep(.main-base-modal-first .c-modal__content) {
    width: 820px;
    padding: 40px;
  }
}

.modal-columns {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
}

@media (--desktop) {
  .modal-columns {
    grid-template-columns: 1fr;
    gap: 40px;
  }
}

.modal-steps-col {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.modal-title-left {
  margin: 0;
  font-weight: 850;
  font-size: 18px;
  color: #1f2937;
}

.modal-steps-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modal-step-item {
  display: flex;
  gap: 12px;
  padding: 14px;
  border: 1px solid #eaecef;
  border-radius: 14px;
  background: #f8f9fb;
}

.modal-step-icon-wrapper {
  position: relative;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  color: #f25c45;
  border: 1px solid #ffd5cf;
  border-radius: 50%;
  background: #fff4f2;
}

.modal-step-svg {
  width: 16px;
  height: 16px;
}

.modal-step-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 14px;
  height: 14px;
  font-weight: 800;
  font-size: 8px;
  color: #ffffff;
  border: 1px solid #ffffff;
  border-radius: 50%;
  background: #f25c45;
}

.modal-step-info {
  flex: 1;
}

.modal-step-title {
  margin-bottom: 2px;
  font-weight: 750;
  font-size: 14px;
  color: #1f2937;
}

.modal-step-desc {
  margin-bottom: 4px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

.modal-form-col {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.modal-title-right {
  margin: 0;
  font-weight: 850;
  font-size: 20px;
  color: #1f2937;
}

.modal-form-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-input {
  width: 100%;
  padding: 14px 16px;
  border: 1.5px solid #eaecef;
  border-radius: 12px;
  font-size: 13.5px;
  transition: all 0.2s;
  background-color: #ffffff;
  color: #1f2937;
}

.form-input:focus {
  outline: none;
  border-color: #f25c45;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgb(242 92 69 / 10%);
}

.form-textarea {
  width: 100%;
  padding: 14px 16px;
  border: 1.5px solid #eaecef;
  border-radius: 12px;
  font-size: 13.5px;
  transition: all 0.2s;
  resize: vertical;
  background-color: #ffffff;
  color: #1f2937;
}

.form-textarea:focus {
  outline: none;
  border-color: #f25c45;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgb(242 92 69 / 10%);
}

.btn-primary {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  background-color: #f25c45;
  box-shadow: 0 4px 14px rgb(242 92 69 / 20%);
  transition: all 0.2s;
  cursor: pointer;
  padding: 14px 0;
  font-size: 15px;
}

.btn-primary:hover {
  transform: translateY(-1.5px);
  box-shadow: 0 6px 18px rgb(242 92 69 / 30%);
}

.btn-primary:disabled {
  background-color: #d1d5db;
  box-shadow: none;
  cursor: not-allowed;
  transform: none;
}
</style>
